import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import SideNav from "./views/common/SideNav";
import Footer from "./views/common/Footer";
import Header from "./views/common/Header";
import Skeletons from "./library/Skeletons";
import MessageToster from "./library/MessageToster";
import { Cookies, useCookies } from "react-cookie";
import { connect, useDispatch } from "react-redux";
import { StoreState } from "./models/reduxModels";
import { userDetails } from "./models/userModels";
import {
  LogOutAction,
  SetIPhoneModeAction,
  userLoginSuccessAction,
} from "./stores/actions/userAction";
import { environment } from "./environment";
import FaceImageRegistrationModal, {
  shouldRequireFaceRegistration,
} from "./components/FaceImageRegistrationModal";
import { isIPhoneDevice, isSafariOnIPhone } from "./utility/deviceDetector";
import IPhoneLayout from "./views/iPhone/IPhoneLayout";
import IPhoneModeSelectionModal from "./views/iPhone/IPhoneModeSelectionModal";
import IPhoneMobileLogin from "./views/iPhone/IPhoneMobileLogin";
import IPhoneSafariRequiredOverlay from "./views/iPhone/IPhoneSafariRequiredOverlay";


import { initSecurityGuards } from "./utility/devToolsGuard";
import ModuleLoaderFallback from "./components/ModuleLoaderFallback";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import lazyWithRetry from "./utility/lazyWithRetry";

// Route-level code splitting with automatic chunk retry
const Dashboard = lazyWithRetry(() => import("./views/dashboard"), "dashboard");
const Employment = lazyWithRetry(() => import("./views/employment"), "employment");
const Organization = lazyWithRetry(() => import("./views/Organization"), "organization");
const Attendance = lazyWithRetry(() => import("./views/Attendance"), "attendance");
const UserManagement = lazyWithRetry(() => import("./views/UserManagement"), "usermanagement");
const LoginRegistration = lazyWithRetry(() => import("./views/LoginRegistration"), "login");
const Home = lazyWithRetry(() => import("./views/home"), "home");

function App({
  user,
  iphoneMode,
  userLoginSuccessAction,
  LogOutAction,
}: AppProps) {
  const [cookies, setCookie, removeCookie] = useCookies([
    "user",
    "iphone_mode",
  ]);
  let cook = new Cookies();
  let location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Enforce right-click / devtools guards only in production builds;
    // keep element and network inspection available on the local dev server.
  // if (process.env.NODE_ENV !== "production") {
  //     return;
  //   }  
    const cleanupSecurity = initSecurityGuards();
    return () => cleanupSecurity();
  }, []);

  const [initialState, SetinitialState] = useState<string | undefined>(
    undefined,
  );
  const logoutEvent = () => {
    removeCookie("user", { path: "/" });
    removeCookie("iphone_mode", { path: "/" });
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("iphone_mode");
    } catch (e) {}
    LogOutAction();
    navigate("/login");
  };

  // Rehydrate Redux user from cookie or localStorage on initial load/refresh (F5)
  useEffect(() => {
    if (!user) {
      let savedUser = cookies.user || cook.get("user");
      if (!savedUser) {
        try {
          const item = localStorage.getItem("user");
          if (item) {
            savedUser = JSON.parse(item);
          }
        } catch (e) {
          console.error("Error reading user from localStorage:", e);
        }
      }
      if (savedUser && (savedUser.userid || savedUser.accessToken)) {
        userLoginSuccessAction(savedUser);
      }
    }
  }, [user, cookies.user, userLoginSuccessAction]);

  const isIPhone = isIPhoneDevice();
  const isAuthenticated = !!user && !!user.userid;
  const rawResetFlag = (user as any)?.is_reset_password;
  const isResetRequired =
    isAuthenticated &&
    (rawResetFlag === 1 ||
      rawResetFlag === true ||
      String(rawResetFlag) === "1" ||
      String(rawResetFlag) === "true");
  const isImageUploadRequired =
    isAuthenticated && shouldRequireFaceRegistration(user?.imageSingUrl);
  const hasUser = isAuthenticated && !isResetRequired && !isImageUploadRequired;
  const isActiveUser = user?.employee_detail?.is_active === 2;

  // Restore iPhone mode from cookie or localStorage if present
  useEffect(() => {
    if (isIPhone) {
      let cookieMode = cook.get("iphone_mode");
      if (!cookieMode) {
        try {
          cookieMode = localStorage.getItem("iphone_mode");
        } catch (e) {}
      }
      if (
        cookieMode &&
        (cookieMode === "attendance" || cookieMode === "other_operations")
      ) {
        if (iphoneMode !== cookieMode) {
          dispatch(SetIPhoneModeAction(cookieMode as any));
        }
      }
    }
  }, [isIPhone, iphoneMode, dispatch]);

  const shouldShowFaceRegistrationModal =
    isAuthenticated && isImageUploadRequired;

  const handleFaceRegistrationComplete = () => {
    removeCookie("user", { path: "/" });
    removeCookie("iphone_mode", { path: "/" });
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("iphone_mode");
    } catch (e) {}
    LogOutAction();
    navigate("/login");
  };

  useEffect(() => {
    if (!!user?.assess_control && !user?.is_reset_password) {
      if (
        user.assess_control.length > 0 &&
        user.assess_control[0].access_control_name !== "dummy_menu"
      ) {
        if (user.employee_detail.is_active === 2) {
          environment.sideNav.forEach((m) => {
            if (m.has_submenu) {
              m.menuItem.forEach((n) => {
                if (n.key === "dashboard") {
                  SetinitialState(n.url);
                }
              });
            } else {
              if (m.key === "dashboard") {
                SetinitialState(m.url);
              }
            }
          });
        } else {
          SetinitialState("/registration-login/");
        }
      } else {
        SetinitialState(`/employment/manage-profile/${user?.employeeId || 0}`);
      }
    } else {
      SetinitialState("/registration-login/");
    }
  }, [
    user?.assess_control,
    user?.is_reset_password,
    user?.employeeId,
    user?.employee_detail?.is_active,
  ]);

  // iPhone-Specific Render Branching (Pre-Login, Profile Image Upload, Password Reset & Post-Login)
  if (isIPhone) {
    // Restrict iPhone access strictly to Native Apple Safari ONLY
    if (!isSafariOnIPhone()) {
      return <IPhoneSafariRequiredOverlay />;
    }
    const isLoginRoute =
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/registration-login");

    // 1. Unauthenticated iPhone User OR Password Reset Required
    if (!isAuthenticated || isResetRequired) {
      // Pre-Login Mode Selection Modal for login routes when mode is not chosen
      if (!iphoneMode && isLoginRoute) {
        return (
          <div className="flex h-screen bg-background font-sans">
            <MessageToster />
            <Skeletons />
            <IPhoneModeSelectionModal
              open={true}
              onClose={() => navigate("/")}
            />
          </div>
        );
      }

      // App-Like Mobile Login & Reset Password for Attendance Mode
      if (iphoneMode === "attendance" && (isLoginRoute || isResetRequired)) {
        return (
          <div className="min-h-screen bg-background font-sans">
            <MessageToster />
            <Skeletons />
            <IPhoneMobileLogin />
          </div>
        );
      }
    }

    // 2. Profile Image Upload Guard (If imageSingUrl is null/empty)
    if (isAuthenticated && isImageUploadRequired) {
      return (
        <div className="flex h-screen bg-background font-sans">
          <MessageToster />
          <Skeletons />
          <FaceImageRegistrationModal
            open={true}
            user={user}
            onUploadComplete={handleFaceRegistrationComplete}
          />
        </div>
      );
    }

    // 3. Authenticated iPhone User -> Attendance Dashboard Layout
    if (hasUser && isActiveUser && iphoneMode === "attendance") {
      return (
        <div className="flex h-screen bg-background font-sans">
          <MessageToster />
          <Skeletons />
          <IPhoneLayout />
        </div>
      );
    }
  }

  // Non-iPhone Users OR iPhone "other_operations" Mode -> Standard Web Experience
  return (
    <div className="flex h-screen bg-background font-sans relative">
      <MessageToster />
      <Skeletons />
      <FaceImageRegistrationModal
        open={shouldShowFaceRegistrationModal}
        user={user}
        onUploadComplete={handleFaceRegistrationComplete}
        onLogout={logoutEvent}
      />

      {/* Mode selection modal popup if on login route without mode */}
      {isIPhone &&
        !hasUser &&
        !iphoneMode &&
        (location.pathname.startsWith("/login") ||
          location.pathname.startsWith("/registration-login")) && (
          <IPhoneModeSelectionModal open={true} onClose={() => navigate("/")} />
        )}

      {/* Pre-Login Mode Switcher Button for iPhone Users on Standard Web Login */}
      {isIPhone && !hasUser && iphoneMode === "other_operations" && (
        <div className="fixed top-3 right-4 z-50">
          <button
            onClick={() => dispatch(SetIPhoneModeAction(undefined))}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3.5 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center space-x-1.5 transition-all active:scale-95 border border-orange-400/30"
            title="Switch iPhone Application Mode"
          >
            <span>📍 Switch Application Mode</span>
          </button>
        </div>
      )}

      <div className="w-auto">{hasUser && isActiveUser && <SideNav />}</div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="sticky top-0 z-50 px-0">
          {hasUser && isActiveUser && <Header logoutEvent={logoutEvent} />}
        </div>
        <main className="flex-1 overflow-auto">
          <ChunkErrorBoundary>
            <Suspense fallback={<ModuleLoaderFallback />}>
              <MainRouter
                hasUser={hasUser}
                initialState={initialState}
                isActive={user?.employee_detail?.is_active}
              />
            </Suspense>
          </ChunkErrorBoundary>
        </main>
        <div className="sticky bottom-0 z-10">{hasUser && <Footer />}</div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: StoreState) => {
  return {
    user: state.user.userDetails,
    iphoneMode: state.user.iphoneMode,
  };
};
const mapDispatchToProps = {
  userLoginSuccessAction,
  LogOutAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);

const MainRouter = ({ hasUser, initialState = "/", isActive }: MainProps) => {
  return (
    <Routes>
      {hasUser ? (
        isActive === 2 ? (
          <>
            <Route path="/dashboard/*" element={<Dashboard />} />
            <Route path="/employment/*" element={<Employment />} />
            <Route path="/organization/*" element={<Organization />} />
            <Route path="/attendance/*" element={<Attendance />} />
            <Route path="/usermanagement/*" element={<UserManagement />} />
          </>
        ) : (
          <Route
            path="/"
            element={<Navigate replace to="/registration-login/login" />}
          />
        )
      ) : (
        <>
          <Route path="/" element={<Home />} />
          <Route
            path="/login"
            element={<Navigate replace to="/registration-login/login" />}
          />
          <Route path="/registration-login/*" element={<LoginRegistration />} />
        </>
      )}
      <Route path="/" element={<Navigate replace to={initialState} />} />
      <Route path="*" element={<Navigate replace to={initialState} />} />
    </Routes>
  );
};

interface MainProps {
  hasUser?: any;
  initialState?: string;
  isActive: number | undefined;
}

interface AppProps {
  user?: userDetails;
  iphoneMode?: "attendance" | "other_operations";
  userLoginSuccessAction?: any;
  LogOutAction?: any;
}
